import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {BACKEND_URL, IS_LOCALHOST, PROFILE, WIDGET_BASE_URL} from "../../../configs";
import styles from "./WidgetDemo.module.css"
import {CustomInputField} from "../../elements/customInputField/CustomInputField";
import {CustomTextAreaField} from "../../elements/customTextAreaField/CustomTextAreaField";
import {SelectDropdown, SelectOption} from "../../elements/select/SelectDropdown";
import {CustomSwitchField} from "../../elements/customSwitchField/CustomSwitchField";
import {Button, ButtonState} from "../../elements/button/Button";
import {LocaleTag} from "../../../api/generated";
import {Arrow} from "../../elements/arrow/Arrow";
import {getLocaleTagTitle} from "../../../utils/enumValueTitleUtil";

type TestWidgetConfig = {
    pageUrl: string;
    locale: string;
    siteContext: string;
    ignoreConversationLimits: boolean;
}

const defaultConfig: TestWidgetConfig = {
    pageUrl: '',
    locale: '',
    siteContext: '',
    ignoreConversationLimits: true,
}

const defaultStopTranscriptSending = true;
const siteContextElementId = 'test-widget-site-context';

const getSiteContextValidationError = (value: string): string | undefined => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return undefined;
    }

    try {
        JSON.parse(trimmedValue);
        return undefined;
    } catch (error) {
        return 'Invalid JSON';
    }
}

const getPageUrlValidationError = (value: string): string | undefined => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return undefined;
    }

    try {
        const pageUrl = new URL(trimmedValue);
        if (pageUrl.protocol === 'http:' || pageUrl.protocol === 'https:') {
            return undefined;
        }
    } catch {
        return 'Invalid URL';
    }

    return 'Invalid URL';
}

export const WidgetDemo: React.FC = () => {
    const [width, setWidth] = useState<string>('400');
    const [height, setHeight] = useState<string>('600');
    const [pageUrl, setPageUrl] = useState<string>(defaultConfig.pageUrl);
    const [locale, setLocale] = useState<string>(defaultConfig.locale);
    const [ignoreConversationLimits, setIgnoreConversationLimits] = useState<boolean>(defaultConfig.ignoreConversationLimits);
    const [stopTranscriptSending, setStopTranscriptSending] = useState<boolean>(defaultStopTranscriptSending);
    const [siteContext, setSiteContext] = useState<string>(defaultConfig.siteContext);
    const [pageUrlError, setPageUrlError] = useState<string>();
    const [siteContextError, setSiteContextError] = useState<string>();
    const [appliedConfig, setAppliedConfig] = useState<TestWidgetConfig>(defaultConfig);
    const [isTestConfigurationOpen, setIsTestConfigurationOpen] = useState<boolean>(false);
    const reloadTimeoutRef = useRef<number>();

    const urlParams = new URLSearchParams(window.location.search);

    const botId = urlParams.get("botId") || ""
    const staticModeKey = "static-mode"
    const isStaticMode = urlParams.get(staticModeKey) === "true"

    const localeOptions = useMemo<SelectOption<string>[]>(() => [
        {label: 'Use assistant default', value: ''},
        ...Object.values(LocaleTag).map(localeTag => ({label: getLocaleTagTitle(localeTag), value: localeTag}))
    ], []);

    const currentConfig: TestWidgetConfig = {
        pageUrl,
        locale,
        siteContext,
        ignoreConversationLimits,
    }

    const hasPendingChanges = JSON.stringify(currentConfig) !== JSON.stringify(appliedConfig);
    const canReloadWidget = hasPendingChanges && !pageUrlError && !siteContextError;

    const sendStopTranscriptSendingEvent = useCallback((value: boolean) => {
        const iframe = document.getElementById('chatbot-widget') as HTMLIFrameElement | null;
        iframe?.contentWindow?.postMessage(
            {
                type: 'update-stop-transcript-sending',
                stopTranscriptSending: value
            },
            WIDGET_BASE_URL
        );
    }, [])

    const removeWidget = useCallback(() => {
        window.postMessage({type: 'remove-event-listeners'}, '*');
        document.getElementById('chatbot-initials-script')?.remove();
        document.getElementById('chatbot-widget')?.remove();
        document.getElementById('widget-mobile-shadow')?.remove();
    }, [])

    const encodeSiteContext = useCallback((value: string): string | undefined => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            setSiteContextError(undefined);
            return undefined;
        }

        const validationError = getSiteContextValidationError(value);
        if (validationError) {
            setSiteContextError(validationError);
            throw new Error(validationError);
        }

        const parsedContext = JSON.parse(trimmedValue);
        setSiteContextError(undefined);
        return btoa(JSON.stringify(parsedContext));
    }, [])

    const appendWidgetScript = useCallback((config: TestWidgetConfig, transcriptSendingStopped: boolean) => {
        const script = document.createElement('script')

        script.setAttribute('type', 'text/javascript')
        script.setAttribute('id', 'chatbot-initials-script')
        script.setAttribute('data-profile', `${PROFILE}`)
        script.setAttribute('src', `${WIDGET_BASE_URL}/script-chatbot.js`)
        script.setAttribute('data-platform-base-url', `${BACKEND_URL}`)
        script.setAttribute('data-bot-id', botId)
        script.setAttribute('data-ignore-conversation-limits', String(config.ignoreConversationLimits))
        script.setAttribute('data-stop-transcript-sending', String(transcriptSendingStopped))

        if (config.pageUrl.trim()) {
            script.setAttribute('data-page-full-url', config.pageUrl.trim())
        }

        if (config.locale) {
            script.setAttribute('data-locale', config.locale)
        }

        // Add container-id if static mode is enabled
        if (isStaticMode) {
            script.setAttribute('data-container-id', 'container')
        }

        document.body.appendChild(script)
        setAppliedConfig({...config});
    }, [botId, isStaticMode])

    const loadWidget = useCallback((config: TestWidgetConfig, transcriptSendingStopped: boolean) => {
        const pageUrlValidationError = getPageUrlValidationError(config.pageUrl);
        if (pageUrlValidationError) {
            setPageUrlError(pageUrlValidationError);
            return;
        }
        setPageUrlError(undefined);

        let encodedSiteContext: string | undefined;
        try {
            encodedSiteContext = encodeSiteContext(config.siteContext);
        } catch (error) {
            return;
        }

        removeWidget();
        document.getElementById(siteContextElementId)?.remove();
        if (encodedSiteContext) {
            const contextElement = document.createElement('div');
            contextElement.setAttribute('id', siteContextElementId);
            contextElement.setAttribute('data-js-context', encodedSiteContext);
            contextElement.style.display = 'none';
            document.body.appendChild(contextElement);
        }

        if (reloadTimeoutRef.current) {
            window.clearTimeout(reloadTimeoutRef.current);
        }

        reloadTimeoutRef.current = window.setTimeout(() => {
            appendWidgetScript(config, transcriptSendingStopped);
            reloadTimeoutRef.current = undefined;
        }, 0);
    }, [appendWidgetScript, encodeSiteContext, removeWidget])

    useEffect(() => {
        loadWidget(defaultConfig, defaultStopTranscriptSending);

        return () => {
            if (reloadTimeoutRef.current) {
                window.clearTimeout(reloadTimeoutRef.current);
            }
            // React StrictMode in development intentionally mounts, unmounts (cleanup), then remounts components.
            // Our cleanup removed the <script> but not the iframe it had injected, so the second mount re‑added another iframe.
            if (!IS_LOCALHOST) {
                removeWidget()
            }
        }
    }, [loadWidget, removeWidget])

    useEffect(() => {
        const handleWidgetMessage = (event: MessageEvent) => {
            if (event.data?.type === 'stop-transcript-sending-loaded') {
                setStopTranscriptSending(!!event.data.stopTranscriptSending);
            }
        }

        window.addEventListener('message', handleWidgetMessage);
        return () => window.removeEventListener('message', handleWidgetMessage);
    }, [])

    const handleReloadWidget = () => {
        const nextPageUrlError = getPageUrlValidationError(pageUrl);
        const nextSiteContextError = getSiteContextValidationError(siteContext);
        setPageUrlError(nextPageUrlError);
        setSiteContextError(nextSiteContextError);

        if (!hasPendingChanges || nextPageUrlError || nextSiteContextError) {
            return;
        }

        loadWidget({pageUrl, locale, siteContext, ignoreConversationLimits}, stopTranscriptSending);
    }

    const handleStopTranscriptSendingChange = (value: boolean) => {
        setStopTranscriptSending(value);
        sendStopTranscriptSendingEvent(value);
    }

    const handlePageUrlChange = (value: string) => {
        setPageUrl(value);
        setPageUrlError(getPageUrlValidationError(value));
    }

    const handleSiteContextChange = (value: string) => {
        setSiteContext(value);
        setSiteContextError(getSiteContextValidationError(value));
    }

    const handleModeChange = (staticModeEnabled: boolean) => {
        const url = new URL(window.location.href);
        if (staticModeEnabled) {
            url.searchParams.set(staticModeKey, "true");
        } else {
            url.searchParams.delete(staticModeKey);
        }
        window.location.href = url.toString();
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.configPanel}>
                <div
                    className={`${styles.section} ${styles.testConfigurationSection} ${isTestConfigurationOpen ? styles.testConfigurationSectionOpen : ""}`}>
                    <div
                        role="button"
                        tabIndex={0}
                        className={styles.sectionHeader}
                        onClick={() => setIsTestConfigurationOpen(!isTestConfigurationOpen)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setIsTestConfigurationOpen(!isTestConfigurationOpen);
                            }
                        }}
                        aria-expanded={isTestConfigurationOpen}
                    >
                        <span className={styles.sectionTitle}>Test configuration</span>
                        <Arrow isUp={isTestConfigurationOpen} onClick={setIsTestConfigurationOpen}/>
                    </div>
                    <div
                        className={`${styles.configurationContent} ${isTestConfigurationOpen ? styles.configurationContentOpen : ""}`}
                        aria-hidden={!isTestConfigurationOpen}
                    >
                        <div className={styles.controlsGrid}>
                            <div className={styles.inputGroupWide}>
                                <CustomInputField
                                    isRequired={false}
                                    title="Page URL"
                                    value={pageUrl}
                                    setValue={handlePageUrlChange}
                                    placeholder="Use actual page URL"
                                    errorMessage={pageUrlError}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <SelectDropdown<string>
                                    title="User Locale"
                                    value={locale}
                                    handleChange={setLocale}
                                    options={localeOptions}
                                    selectFirstOptionByDefault={false}
                                />
                            </div>
                        </div>
                        <div className={styles.siteContextRow}>
                            <CustomTextAreaField
                                isRequired={false}
                                title="Site Context JSON"
                                value={siteContext}
                                setValue={handleSiteContextChange}
                                maxLength={30000}
                                placeholder='{"site":{"language":"en","country":"GB"}}'
                                errorMessage={siteContextError}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.embeddedModeControls}>
                <div className={styles.inlineToggle}>
                    <span className={styles.fieldTitle}>Embedded mode</span>
                    <CustomSwitchField
                        textOnTrue="On"
                        textOnFalse="Off"
                        value={isStaticMode}
                        toggleValue={handleModeChange}
                    />
                </div>
                <div className={styles.inlineToggle}>
                    <span className={styles.fieldTitle}>Ignore conversation limits</span>
                    <CustomSwitchField
                        textOnTrue="On"
                        textOnFalse="Off"
                        value={ignoreConversationLimits}
                        toggleValue={setIgnoreConversationLimits}
                    />
                </div>
                {isStaticMode && (
                    <div className={styles.inlineDimensions}>
                        <div className={styles.dimensionGroup}>
                            <CustomInputField
                                isRequired={false}
                                title="Width (px)"
                                value={width}
                                setValue={setWidth}
                                placeholder="400"
                            />
                        </div>
                        <div className={styles.dimensionGroup}>
                            <CustomInputField
                                isRequired={false}
                                title="Height (px)"
                                value={height}
                                setValue={setHeight}
                                placeholder="600"
                            />
                        </div>
                    </div>
                )}
                <div className={styles.reloadSection}>
                    <div className={styles.inlineToggle}>
                        <span className={styles.fieldTitle}>Do not send transcript email</span>
                        <CustomSwitchField
                            textOnTrue="On"
                            textOnFalse="Off"
                            value={stopTranscriptSending}
                            toggleValue={handleStopTranscriptSendingChange}
                        />
                    </div>
                    <Button
                        title="Reload widget"
                        state={canReloadWidget ? ButtonState.PRIMARY : ButtonState.DISABLED}
                        handleClick={handleReloadWidget}
                    />
                    {hasPendingChanges && !pageUrlError && !siteContextError && (
                        <div className={styles.reloadWarning}>
                            You must click Reload widget to apply the test configuration changes.
                        </div>
                    )}
                    {pageUrlError && (
                        <div className={styles.reloadWarning}>
                            Fix Page URL before reloading the widget.
                        </div>
                    )}
                    {siteContextError && (
                        <div className={styles.reloadWarning}>
                            Fix Site Context JSON before reloading the widget.
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.widgetArea}>
                {isStaticMode && (
                    <div
                        id="container"
                        className={styles.container}
                        style={{
                            width: `${width}px`,
                            height: `${height}px`
                        }}
                    />
                )}
            </div>
        </div>
    )
}
